import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@muzly/lib/utils';
import { ChevronLeft } from 'lucide-react';

import { CloseDiagnosisButton } from './CloseDiagnosisButton';
import { useOnboarding } from '../context';

/**
 * 온보딩 각 단계를 감싸는 셸 — 상단 크롬(뒤로 · 진행 바 · 닫기)과 하단 CTA.
 * 단계 화면은 children으로 들어오고 진행·전이는 컨텍스트가 소유한다.
 *
 * **크롬은 시안 §0의 `설문 헤더`다**: 좌 `<` · 가운데 진행 바 하나(6px) · 우 `×`.
 * 예전에는 뒤로가기가 자기 줄을 차지하고 그 아래 세그먼트 점이 깔렸는데, 시안은 한 줄에
 * 셋을 나란히 두고 진행을 **연속 막대 하나**로 그린다. 세그먼트로 쪼개면 단계가 13개라
 * 점이 13개 생겨 막대가 아니라 눈금자로 보인다.
 *
 * `×`는 플로우가 정한다(`flow.closable`): 초기 설정은 프로필 없이는 갈 곳이 없어 어느
 * 단계에도 없고(시안 step1에 `×`가 없는 이유), 진단은 첫 단계부터 홈으로 나갈 수 있다.
 */

export const StepShell = ({
  children,
  canAdvance = true,
  hideCta = false,
  bare = false,
  ctaLabelKey,
}: {
  children: ReactNode;
  /** 하단 CTA 활성화 여부 — 필수 응답 미충족 단계에서 false. */
  canAdvance?: boolean;
  /** 기본 CTA 숨김 — 자체 전이를 가진 단계(녹음·결과)가 쓴다. */
  hideCta?: boolean;
  /**
   * 셸의 크롬(뒤로가기·진행 바)과 **좌우·하단 여백까지** 뺀다. 측정 화면은 점선이 화면을
   * 위아래로 관통하고 완료 버튼이 풀 너비라, 여백이 남으면 시안이 성립하지 않는다.
   *
   * 여백까지 빼므로 `bare` 단계의 화면은 **자기 여백을 스스로 갖는다** — 같은 단계 안에서도
   * 측정 화면은 가장자리까지, 가이드 카드는 여백 안에 놓인다(VoiceRecordStep이 감싼다).
   */
  bare?: boolean;
  /** CTA 문구를 단계가 정한다(`진단하기`·`계속`…). 없으면 `다음`. 마지막 단계의 `next`는
   *  플로우를 나간다(프로바이더 `onComplete`). */
  ctaLabelKey?: string;
}) => {
  const { t } = useTranslation();
  const { flow, progress, isFirst, next, back } = useOnboarding();
  const percent = Math.round((progress.current / progress.total) * 100);

  return (
    <div
      // 상태바 뒤판도 흰색으로 — styles.css `[data-status-backplate]`.
      data-surface="lowest"
      className={cn(
        // 시안의 설문 화면은 **흰 바탕**이다. 앱 기본 배경(#f9f9f9)에 두면 흰 카드가
        // 배경과 거의 같은 색이 되어 테두리 없이는 카드가 안 보인다.
        // 상단 여백은 헤더가 직접 든다(아래) — 여기 두면 `sticky` 헤더가 노치 밑에
        // 붙고 그 위에 빈 띠가 남는다. `bare` 는 헤더가 없으므로 루트가 그대로 든다.
        'flex min-h-dvh flex-col bg-[hsl(var(--surface-lowest))] text-foreground',
        bare && 'pt-[calc(var(--safe-area-top,0px)+0.5rem)]',
        !bare && 'px-4',
      )}
    >
      {!bare && (
        /* 진행 바와 나가는 문(`×`)은 스크롤을 타고 사라지면 안 된다 — 선택지 목록이
            긴 단계에서 "얼마나 남았나"와 중단 수단이 같이 없어졌다. 하단 CTA 와 같은
            폭을 쓰도록 `-mx-4 px-4`(루트가 `px-4`)로 좌우를 되돌린다. */
        <header
          className="sticky top-0 z-30 -mx-4 flex items-center gap-4 bg-[hsl(var(--surface-lowest))] px-4 pb-2"
          style={{ paddingTop: 'calc(var(--safe-area-top, 0px) + 0.5rem)' }}
        >
          <button
            type="button"
            onClick={back}
            aria-label={t('web.common.back')}
            className={cn(
              'flex h-11 w-11 shrink-0 items-center justify-center -ml-3 text-foreground',
              isFirst && 'invisible',
            )}
          >
            <ChevronLeft className="h-6 w-6" strokeWidth={2.5} />
          </button>
          {/* 연속 막대 하나 — 시안 §0(트랙 `#EAEAEC` / 채움 `#FB233B`, 높이 6px). */}
          <div
            className="h-1.5 flex-1 overflow-hidden rounded-full bg-[var(--bk-100)]"
            role="progressbar"
            aria-label={t('web.common.progress', {
              current: progress.current,
              total: progress.total,
            })}
            aria-valuenow={progress.current}
            aria-valuemin={0}
            aria-valuemax={progress.total}
          >
            <span
              data-testid="onboarding-progress-fill"
              className="block h-full rounded-full bg-primary transition-[width] duration-300"
              style={{ width: `${percent}%` }}
            />
          </div>
          {/* 나갈 문이 없는 플로우도 자리는 비워 둔다 — 진행 바가 가운데에 남아야 한다.
              없애면 막대가 오른쪽으로 늘어난다. */}
          {flow.closable ? (
            <CloseDiagnosisButton />
          ) : (
            <span className="h-11 w-11 shrink-0" aria-hidden="true" />
          )}
        </header>
      )}

      <main className={cn('flex flex-1 flex-col', !bare && 'pt-6')}>
        {children}
      </main>

      {!hideCta && (
        // 시안 §0의 하단 CTA — pill 버튼 + 위쪽 흰색 그라디언트 페이드. 페이드가 있어야
        // 스크롤되는 선택지 목록이 버튼 밑으로 잘려 들어가는 게 아니라 사라져 보인다.
        <div className="sticky bottom-0 -mx-4 bg-gradient-to-b from-transparent to-[hsl(var(--surface-lowest))] px-4 pb-8 pt-7">
          <button
            type="button"
            onClick={next}
            disabled={!canAdvance}
            className={cn(
              'h-[50px] w-full rounded-full text-center text-base font-bold tracking-[0.08px] transition-colors',
              canAdvance
                ? 'bg-[var(--bk-900)] text-white'
                : // #cfd0d3 배경에 흰 텍스트는 실측 대비가 ~1.5:1이라 문구 자체가 안
                  // 읽힌다 — 같은 팔레트의 더 짙은 계단(#3a3c40, ~7.2:1)으로 바꿔
                  // "왜 안 눌리는지" 읽을 수는 있게 한다(비활성 대비는 WCAG 면제지만
                  // 가독성 문제는 남는다).
                  'bg-[var(--bk-300)] text-[var(--bk-800)]',
            )}
          >
            {t(ctaLabelKey ?? 'web.common.next')}
          </button>
        </div>
      )}
    </div>
  );
};
